import type { QueryClient } from '@tanstack/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { persistQueryClientSubscribe } from '@tanstack/react-query-persist-client';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  buildPersistSubscribeOptions,
  createQueryPersister,
  type AppQueryPersister,
} from './queryPersistOptions';
import { createQueryClient } from './queryClient';

type Props = {
  children: ReactNode;
  queryClient?: QueryClient;
  /** Prefer the instance from {@link bootstrapPersistedQueryClient} so storage is shared. */
  persister?: AppQueryPersister;
};

/**
 * - **Production:** pass `queryClient` + `persister` from {@link bootstrapPersistedQueryClient}
 *   (cache already restored before first paint).
 * - **Tests / HMR:** omit both to get a fresh in-memory client; a new persister still writes if run
 *   in a browser (usually irrelevant in jsdom).
 */
export const QueryProvider = ({
  children,
  queryClient: queryClientProp,
  persister: persisterProp,
}: Props) => {
  const [queryClient] = useState(() => queryClientProp ?? createQueryClient());
  const persister = useMemo(
    () => persisterProp ?? createQueryPersister(),
    [persisterProp],
  );

  useEffect(() => {
    return persistQueryClientSubscribe(
      buildPersistSubscribeOptions(queryClient, persister),
    );
  }, [queryClient, persister]);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};
